// WP-5.1 (RE-2, Snapshot): position-over-time series query + the nightly job's manual trigger.
import type { CurrencyCode } from '@pfos/engine';
import {
  snapshotRunNowResponseSchema,
  snapshotSeriesResponseSchema,
  type SnapshotRunNowResponse,
  type SnapshotSeriesResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const SNAPSHOTS_KEY = ['snapshots'] as const;

export interface SnapshotFilters {
  currency: CurrencyCode;
  from?: string;
  to?: string;
}

export function useSnapshotSeries(
  filters: SnapshotFilters,
): UseQueryResult<SnapshotSeriesResponse> {
  const params = new URLSearchParams({ currency: filters.currency });
  if (filters.from !== undefined) params.set('from', filters.from);
  if (filters.to !== undefined) params.set('to', filters.to);
  return useQuery({
    queryKey: [...SNAPSHOTS_KEY, filters],
    queryFn: async () =>
      snapshotSeriesResponseSchema.parse(await api.get(`/api/v1/snapshots?${params.toString()}`)),
  });
}

/** The pg-boss scheduler's own nightly target — also callable manually (an owner "refresh
 * now" action, or an ops cron), the same shape as the recurring run-due mutation. */
export function useRunSnapshotNow(): UseMutationResult<SnapshotRunNowResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      snapshotRunNowResponseSchema.parse(await api.post('/api/v1/snapshots/run-now')),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SNAPSHOTS_KEY });
    },
  });
}
